import type { components } from '@/api/schema';

import { capitalize, humanize, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

export type Role = Schemas['JobPackageTask']['responsibleRole'];
export type UserStatus = Schemas['UpdateStaffRequest']['status'];
export type ActorType = Schemas['AuditEvent']['actorType'];

/** The capabilities the server authorises against; the schema carries them as plain strings. */
export type Permission =
  | 'CUSTOMER_READ'
  | 'CUSTOMER_WRITE'
  | 'CUSTOMER_MERGE'
  | 'CUSTOMER_EXPORT'
  | 'JOB_READ'
  | 'JOB_WRITE'
  | 'JOB_SCHEDULE'
  | 'JOB_STATUS_UPDATE'
  | 'JOB_FIELD_EXECUTE'
  | 'DISPATCH_BOARD_READ'
  | 'DISPATCH_BOARD_MANAGE'
  | 'PRICEBOOK_READ'
  | 'PRICEBOOK_MANAGE'
  | 'INVENTORY_READ'
  | 'INVENTORY_ADJUST'
  | 'INVOICE_READ'
  | 'INVOICE_WRITE'
  | 'PAYMENT_RECORD'
  | 'PAYROLL_READ'
  | 'PAYROLL_RUN'
  | 'FINANCE_SETUP_MANAGE'
  | 'TEAM_READ'
  | 'TEAM_MANAGE'
  | 'TEAM_AVAILABILITY_MANAGE'
  | 'COMPENSATION_MANAGE'
  | 'LEAD_INTAKE'
  | 'OPPORTUNITY_READ'
  | 'OPPORTUNITY_WRITE'
  | 'OPPORTUNITY_OWN'
  | 'OPPORTUNITY_ROUTE'
  | 'OPPORTUNITY_MANAGE'
  | 'CONSULTATION_CONDUCT'
  | 'APPOINTMENT_SCHEDULE'
  | 'PROPOSAL_READ'
  | 'PROPOSAL_DRAFT'
  | 'PROPOSAL_PRESENT'
  | 'PRICING_QUOTE'
  | 'PRICING_EXCEPTION_APPROVE'
  | 'PRICING_CONTROLS_MANAGE'
  | 'JOB_PACKAGE_MANAGE'
  | 'JOB_PACKAGE_OVERRIDE'
  | 'REPLACEMENT_HANDOFF_SUBMIT'
  | 'SALES_REPORTING_READ'
  | 'AI_ASSIST_READ'
  | 'AI_DRAFT_REQUEST'
  | 'AI_ACTION_APPROVE'
  | 'AUDIT_READ'
  | 'USER_MANAGE'
  | 'TENANT_PROVISION'
  | 'APPROVAL_LINK_VIEW'
  | 'APPROVAL_LINK_RESPOND';

/** Time off has no enum in the schema; the team service accepts exactly these. */
export type TimeOffKind = 'VACATION' | 'SICK' | 'OTHER';
export type TimeOffStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** How a pay plan pays; the schema leaves it open and the compensation service accepts these. */
export type CompensationModel = 'HOURLY' | 'PERCENT_OF_JOB' | 'FLAT_PER_JOB' | 'HYBRID';

// Mirrors Role.getDisplayName() on the server.
export const ROLE_LABELS: Record<Role, string> = {
  OWNER: 'Owner',
  ACCOUNTING: 'Accounting',
  SALES_AGENT: 'Comfort Consultant',
  SALES_MANAGER: 'Sales Manager',
  SALES_COORDINATOR: 'Sales Coordinator',
  SERVICE_TECHNICIAN: 'Service Technician',
  CSR_DISPATCHER: 'CSR / Dispatcher',
  CUSTOMER: 'Customer / Decision-maker',
};

// Each phrase finishes the sentence "Your roles do not let you ...".
export const PERMISSION_PHRASES: Record<Permission, string> = {
  CUSTOMER_READ: 'view customer records',
  CUSTOMER_WRITE: 'create and change customer records',
  CUSTOMER_MERGE: 'merge duplicate customers',
  CUSTOMER_EXPORT: 'export the customer list',
  JOB_READ: 'view jobs',
  JOB_WRITE: 'create and change jobs',
  JOB_SCHEDULE: 'schedule jobs and assign technicians',
  JOB_STATUS_UPDATE: 'move a job to its next status',
  JOB_FIELD_EXECUTE: 'work a job in the field',
  DISPATCH_BOARD_READ: 'view the dispatch board',
  DISPATCH_BOARD_MANAGE: 'rearrange the dispatch board',
  PRICEBOOK_READ: 'view the pricebook',
  PRICEBOOK_MANAGE: 'manage the pricebook',
  INVENTORY_READ: 'view stock levels',
  INVENTORY_ADJUST: 'adjust stock',
  INVOICE_READ: 'view invoices',
  INVOICE_WRITE: 'create and change invoices',
  PAYMENT_RECORD: 'record payments',
  PAYROLL_READ: 'view payroll',
  PAYROLL_RUN: 'run payroll',
  FINANCE_SETUP_MANAGE: 'change the default tax rate and invoice payment terms',
  TEAM_READ: 'view the team',
  TEAM_MANAGE: 'manage team profiles and time off',
  TEAM_AVAILABILITY_MANAGE: 'mark crew members unavailable',
  COMPENSATION_MANAGE: 'manage pay plans and commission rules',
  LEAD_INTAKE: 'log new leads',
  OPPORTUNITY_READ: 'view opportunities',
  OPPORTUNITY_WRITE: 'change opportunities and move them along the pipeline',
  OPPORTUNITY_OWN: 'be the named owner of an opportunity',
  OPPORTUNITY_ROUTE: 'route opportunities between owners',
  OPPORTUNITY_MANAGE: 'manage every opportunity',
  CONSULTATION_CONDUCT: 'run a consultation',
  APPOINTMENT_SCHEDULE: 'schedule sales visits',
  PROPOSAL_READ: 'view proposals',
  PROPOSAL_DRAFT: 'write and revise proposals',
  PROPOSAL_PRESENT: 'present proposals to customers',
  PRICING_QUOTE: 'quote prices to customers',
  PRICING_EXCEPTION_APPROVE: 'approve pricing exceptions',
  PRICING_CONTROLS_MANAGE: 'manage pricing controls',
  JOB_PACKAGE_MANAGE: 'prepare sold jobs for installation',
  JOB_PACKAGE_OVERRIDE: 'schedule an installation that has open checks',
  REPLACEMENT_HANDOFF_SUBMIT: 'hand a replacement opportunity to sales',
  SALES_REPORTING_READ: 'read sales reports',
  AI_ASSIST_READ: 'ask the assistant to read and summarise records',
  AI_DRAFT_REQUEST: 'ask the assistant for drafts',
  AI_ACTION_APPROVE: 'approve changes the assistant proposes',
  AUDIT_READ: 'read the audit trail',
  USER_MANAGE: 'manage users',
  TENANT_PROVISION: 'create workspaces',
  APPROVAL_LINK_VIEW: 'view a customer approval link',
  APPROVAL_LINK_RESPOND: 'respond to a customer approval link',
};

export const TIME_OFF_KIND_LABELS: Record<TimeOffKind, string> = {
  VACATION: 'Vacation',
  SICK: 'Sick leave',
  OTHER: 'Other',
};

export const TIME_OFF_STATUS_LABELS: Record<TimeOffStatus, string> = {
  PENDING: 'Waiting for approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const COMPENSATION_MODEL_LABELS: Record<CompensationModel, string> = {
  HOURLY: 'Hourly',
  PERCENT_OF_JOB: 'Percent of the job',
  FLAT_PER_JOB: 'Flat amount per job',
  HYBRID: 'Hourly plus job pay',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  ACTIVE: 'Active',
  INVITED: 'Invited',
  SUSPENDED: 'Suspended',
};

export const ACTOR_TYPE_LABELS: Record<ActorType, string> = {
  HUMAN: 'Person',
  AI_WITH_APPROVER: 'Assistant, approved by a person',
  SYSTEM: 'System',
};

/** The time-off kinds as `{ value, label }`, for a select. */
export const TIME_OFF_KIND_OPTIONS = optionsOf(TIME_OFF_KIND_LABELS);

/** The pay models as `{ value, label }`, for a select. */
export const COMPENSATION_MODEL_OPTIONS = optionsOf(COMPENSATION_MODEL_LABELS);

export function roleLabel(role: string | null | undefined): string {
  return labelOf(ROLE_LABELS, role);
}

/**
 * A role as the settings screens name it.
 *
 * The server's role picker calls the owner "Administrator (owner)", because there the role is the
 * one that grants every capability; the account page has to read the same as the picker beside it.
 *
 * @param role a role code
 * @returns the name settings uses for it
 */
export function settingsRoleLabel(role: string | null | undefined): string {
  return role === 'OWNER' ? 'Administrator (owner)' : roleLabel(role);
}

/**
 * What a permission lets a person do, as a verb phrase: "view the dispatch board".
 *
 * @param permission a permission code
 * @returns the phrase, in lower case so it can finish a sentence
 */
export function permissionPhrase(permission: string): string {
  return Object.hasOwn(PERMISSION_PHRASES, permission)
    ? PERMISSION_PHRASES[permission as Permission]
    : humanize(permission).toLowerCase();
}

/**
 * The same phrase, capitalized for a list: "View the dispatch board".
 *
 * @param permission a permission code
 * @returns the phrase as a list item
 */
export function permissionTitle(permission: string): string {
  return capitalize(permissionPhrase(permission));
}

export function timeOffKindLabel(kind: string | null | undefined): string {
  return labelOf(TIME_OFF_KIND_LABELS, kind);
}

export function timeOffStatusLabel(status: string | null | undefined): string {
  return labelOf(TIME_OFF_STATUS_LABELS, status);
}

export function compensationModelLabel(model: string | null | undefined): string {
  return labelOf(COMPENSATION_MODEL_LABELS, model);
}

export function userStatusLabel(status: string | null | undefined): string {
  return labelOf(USER_STATUS_LABELS, status);
}

export function actorTypeLabel(type: string | null | undefined): string {
  return labelOf(ACTOR_TYPE_LABELS, type);
}
